import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, Clock, Info } from "lucide-react";

/**
 * Public party acceptance page — clicking the invitation email link lands
 * here. Renders sanitised transaction preview; clicking the CTA records
 * explicit acceptance separately (with IP, UA, timestamp, audit trail).
 * Not treated as legal acceptance until the user clicks Confirm.
 */
export default function EscrowAccept() {
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const email = (params.get("email") || "").toLowerCase();
  const token = params.get("token");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      if (!ref || !email || !token) { setError("Invalid link"); return; }
      try {
        const r = await api.get(`/escrow-v2/${ref}/invitation`, { params: { email, token } });
        setD(r.data);
        if (r.data.status === "accepted") setAccepted(true);
      } catch (e) {
        setError(e?.response?.data?.detail || "Invitation link is invalid or expired");
      }
    })();
  }, [ref, email, token]);

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/escrow-v2/${ref}/accept`, { email, token });
      setAccepted(true);
      toast.success("Acceptance recorded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Acceptance failed");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="kicker">THOM Escrow™ · Party invitation</div>
        <h1 className="display text-3xl mt-1">Review & confirm participation</h1>

        {error && (
          <div className="mt-6 border border-red-500/40 bg-red-500/5 p-6 rounded-sm text-sm text-red-700" data-testid="accept-error">{error}</div>
        )}

        {d && (
          <div className="mt-6 border border-border bg-card p-6" data-testid="accept-view">
            <div className="mono text-[11px] uppercase tracking-widest text-primary">{d.reference}</div>
            <h2 className="text-xl font-semibold mt-1">{d.asset_description}</h2>
            <div className="text-[12.5px] text-muted-foreground mt-0.5">{d.country} · <span className="mono uppercase text-[10px]">{d.asset_type}</span></div>

            <div className="grid grid-cols-2 gap-x-6 gap-y-3 mt-5 text-[12.5px]">
              <Row l="Your role" v={d.role.replace(/_/g, " ")} cap />
              <Row l="Recipient" v={email} mono />
              <Row l="Transaction value" v={`${d.currency} ${(d.amount_total || 0).toLocaleString()}`} mono />
              <Row l="Workflow" v={d.workflow_label} />
              <Row l="Current stage" v={d.current_stage.replace(/_/g, " ")} cap />
              <Row l="Invitation expires" v={new Date(d.expires_at).toLocaleString()} />
              {d.accepted_at && <Row l="Acceptance recorded" v={new Date(d.accepted_at).toLocaleString()} />}
            </div>

            <div className="mt-6 border border-amber-500/30 bg-amber-500/5 p-3 rounded-sm text-[12px] flex items-start gap-2">
              <Info className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
              <span>
                Clicking <em>Confirm participation</em> records explicit acceptance with your IP address and timestamp.
                It is not a substitute for statutory approvals or legal title verification, which remain with the
                relevant competent authorities.
              </span>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {accepted ? (
                <Button size="lg" disabled className="cursor-default" data-testid="accept-done">
                  <ShieldCheck className="h-4 w-4 mr-2" /> Participation confirmed
                </Button>
              ) : (
                <Button size="lg" onClick={confirm} disabled={busy} data-testid="accept-confirm">
                  {busy ? "Recording…" : "Confirm participation"}
                </Button>
              )}
              <a href="mailto:support@thomverify.africa">
                <Button size="lg" variant="outline">Contact support</Button>
              </a>
            </div>
          </div>
        )}

        {!d && !error && (
          <div className="mt-6 text-sm text-muted-foreground flex items-center gap-2">
            <Clock className="h-3.5 w-3.5" /> Loading invitation…
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

const Row = ({ l, v, mono, cap }) => (
  <>
    <div className="mono text-[9.5px] uppercase tracking-widest text-muted-foreground">{l}</div>
    <div className={`${mono ? "mono text-primary" : ""} ${cap ? "capitalize" : ""}`}>{v || "—"}</div>
  </>
);
